import { createRoute, Link, redirect } from '@tanstack/react-router';
import {
  CodeBlock,
  EmptyState,
  Mono,
  PageHeader,
  ResourceTable,
  SectionCard,
  StatusBadge,
} from '@verydia/ui';
import { FileClock, KeyRound, Layers, Webhook } from 'lucide-react';
import { rootRoute } from './root.tsx';
import { useVendorBreadcrumbs } from '../lib/breadcrumbs.ts';
import { PlaceholderPage } from '../components/placeholder-page.tsx';
import { EnvironmentsPanel } from '../components/environments.tsx';
import { useConsoleContext } from '../lib/console-context.tsx';
import { apiBaseUrl } from '../lib/api.ts';

/**
 * `/developers` is a navigation grouping, not a page — the sidebar lists its
 * children directly. But it is a URL a developer will type, and it appeared in
 * breadcrumbs, so landing on "Page not found" was a dead end of our own making.
 * It redirects to the first child instead.
 */
export const developersIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/developers',
  beforeLoad: () => {
    throw redirect({ to: '/developers/api-keys' });
  },
  component: () => null,
});

export const apiKeysRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/developers/api-keys',
  component: () => (
    <PlaceholderPage
      title="API Keys"
      description="Credentials for the management API. Each key is bound to one environment for its entire life and cannot be moved between environments."
      breadcrumbs={useVendorBreadcrumbs({ label: 'Developers' }, { label: 'API Keys' })}
      icon={<KeyRound />}
      emptyTitle="No API keys yet"
      emptyDescription="Keys are shown once at creation and stored only as a hash, so a database compromise never yields a working credential. Production keys carry a vc_live_ prefix; every other environment issues vc_test_."
      availability="This capability is not available yet."
    />
  ),
});

export const webhooksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/developers/webhooks',
  component: () => (
    <PlaceholderPage
      title="Webhooks"
      description="Signed HTTPS callbacks for licensing events: created, updated, activated, expiring, expired, suspended and revoked."
      breadcrumbs={useVendorBreadcrumbs({ label: 'Developers' }, { label: 'Webhooks' })}
      icon={<Webhook />}
      emptyTitle="No webhook endpoints yet"
      emptyDescription="Deliveries are signed, retried with exponential backoff, dead-lettered after exhaustion and replayable from the delivery log."
      availability="This capability is not available yet."
    />
  ),
});

export const requestLogsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/developers/request-logs',
  component: () => (
    <PlaceholderPage
      title="Request Logs"
      description="Every API request made against this environment, searchable by request ID, route, status and license."
      breadcrumbs={useVendorBreadcrumbs({ label: 'Developers' }, { label: 'Request Logs' })}
      icon={<FileClock />}
      emptyTitle="No requests recorded"
      emptyDescription="Request logs are captured for authenticated API traffic. This deployment exposes only unauthenticated platform endpoints, so there is nothing to record yet."
      availability="This capability is not available yet."
    />
  ),
});

/* -------------------------------------------------------------------------
   Environments — a real page, not a placeholder.

   The environment model exists in the domain from day one, so the page shows
   the actual model with its real isolation semantics. What is missing is
   provisioning, and the page says exactly that rather than implying otherwise.
   ------------------------------------------------------------------------- */

export const environmentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/developers/environments',
  component: EnvironmentsPage,
});

/**
 * Documentation of the model, not live data.
 *
 * This table used to render the console's environment CONTEXT, which was seeded
 * with three fabricated rows — so it showed Development, Pilot and Production
 * for organizations that had no products at all. It is a reference table, so it
 * owns its own content.
 *
 * The credential prefix is keyed on classification, not on the name: a vendor
 * may call production "live", and a `vc_live_` key must follow the thing that
 * decides blast radius.
 */
function EnvironmentsPage() {
  const context = useConsoleContext();
  const organizationId = context.organization?.id ?? null;
  const product = context.product;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Environments"
        description="Environments are the isolation boundary a credential can never cross. A key issued for Development cannot read, create or modify anything in Production, even within the same product."
        breadcrumbs={useVendorBreadcrumbs({ label: 'Developers' }, { label: 'Environments' })}
        linkComponent={Link}
      />

      {/*
        The same environments the product's own tab shows, from the same query.

        This page used to render a fixed table of Development, Pilot and
        Production — a second, fabricated environment model, shown to
        organizations that had no products at all. There is one source of
        environment data now, and it is reached through a product, because an
        environment belongs to one.
      */}
      {organizationId !== null && product !== null ? (
        <EnvironmentsPanel
          organizationId={organizationId}
          productId={product.id}
          productName={product.name}
          productIsActive
        />
      ) : (
        <SectionCard>
          <EmptyState
            icon={<Layers />}
            title="Select a product"
            description="Environments belong to a product. Choose one in the context bar above, or open it from the product list."
            action={
              <Link
                to="/products"
                className={
                  'inline-flex h-8 items-center rounded-[var(--vc-radius-md)] ' +
                  'bg-[var(--vc-accent)] px-3 text-[13px] font-medium ' +
                  'text-[var(--vc-accent-contrast)] hover:bg-[var(--vc-accent-hover)] ' +
                  'focus-visible:outline-2 focus-visible:outline-offset-2 ' +
                  'focus-visible:outline-[var(--vc-focus-ring)]'
                }
              >
                Go to products
              </Link>
            }
          />
        </SectionCard>
      )}

      <SectionCard title="Why the credential prefix is visible">
        <p className="max-w-[80ch] text-[13px] leading-relaxed text-[var(--vc-text-muted)]">
          Credentials for a production-classified environment carry <Mono>vc_live_</Mono>;
          everything else carries <Mono>vc_test_</Mono>. A test credential pasted into production
          configuration is one of the most common — and most expensive — integration mistakes.
          Making the distinction visible in the secret itself means it can be caught by eye in a
          config file, a pull request or a screenshot, without needing to look the key up.
        </p>
        <p className="mt-3 max-w-[80ch] text-[13px] leading-relaxed text-[var(--vc-text-muted)]">
          The prefix follows the{' '}
          <strong className="font-medium text-[var(--vc-text)]">classification</strong>, not the
          name: a vendor may call production “live”, and a live key must follow the thing that
          decides blast radius.
        </p>
      </SectionCard>
    </div>
  );
}

/* -------------------------------------------------------------------------
   SDKs — also real. The examples below run against this deployment today.
   ------------------------------------------------------------------------- */

export const sdksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/developers/sdks',
  component: SdksPage,
});

interface SdkRow {
  language: string;
  packageName: string;
  available: boolean;
}

const SDK_ROADMAP: readonly SdkRow[] = [
  { language: 'TypeScript / Node', packageName: '@verydia/sdk', available: false },
  { language: 'Python', packageName: 'verydia-control', available: false },
  { language: 'Go', packageName: 'verydia-control-go', available: false },
  { language: 'CLI', packageName: 'verydia', available: false },
  { language: '.NET', packageName: 'Verydia.Control', available: false },
  {
    language: 'Java / Kotlin',
    packageName: 'com.verydia:control',
    available: false,
  },
  { language: 'Swift', packageName: 'VerydiaControl', available: false },
];

function SdksPage() {
  const curlExample = [
    `curl -sS ${apiBaseUrl}/v1/platform/info \\`,
    '  -H "Accept: application/json" -D -',
  ].join('\n');

  const typescriptExample = [
    "import { VerydiaControlClient } from '@verydia/sdk';",
    '',
    'const verydia = new VerydiaControlClient({',
    `  baseUrl: '${apiBaseUrl}',`,
    '});',
    '',
    'const { data, requestId } = await verydia.platformInfo();',
    '',
    // Shows the real shape. It previously read `runtime_api` as a boolean
    // "false until VC-4" — wrong twice over once capabilities became states,
    // and a sample that will not compile is worse than no sample.
    "console.log(data.capabilities.runtime_validation); // 'LIVE' | 'NOT_AVAILABLE'",
    'console.log(requestId);                            // req_… — quote this in support',
  ].join('\n');

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="SDKs"
        description="OpenAPI 3.1 is the canonical contract for the Verydia Control API. Every client library is generated from, or verified against, that document."
        breadcrumbs={useVendorBreadcrumbs({ label: 'Developers' }, { label: 'SDKs' })}
        linkComponent={Link}
      />

      <div className="grid gap-5 xl:grid-cols-2">
        <SectionCard
          title="Call the API directly"
          description="Both endpoints available at this stage are unauthenticated."
        >
          <CodeBlock language="shell" code={curlExample} />
          <p className="mt-3 text-[12px] leading-relaxed text-[var(--vc-text-subtle)]">
            Every response carries an <Mono>X-Request-ID</Mono> header. Include it when reporting a
            problem — it is the key to the full server-side trace of your request.
          </p>
        </SectionCard>

        <SectionCard title="TypeScript" description="The client that this console itself uses.">
          <CodeBlock language="typescript" code={typescriptExample} />
          <p className="mt-3 text-[12px] leading-relaxed text-[var(--vc-text-subtle)]">
            Not yet published to a registry. It lives in this repository at{' '}
            <Mono>packages/sdk</Mono> and is consumed through the workspace.
          </p>
        </SectionCard>
      </div>

      <SectionCard title="Client library roadmap" flush>
        <ResourceTable<SdkRow>
          caption="Planned Verydia Control client libraries"
          rows={SDK_ROADMAP}
          rowKey={(row) => row.language}
          columns={[
            {
              key: 'language',
              header: 'Language',
              render: (row) => <span className="font-medium">{row.language}</span>,
            },
            { key: 'package', header: 'Package', render: (row) => <Mono>{row.packageName}</Mono> },
            {
              key: 'status',
              header: 'Status',
              align: 'right',
              render: (row) => (
                <StatusBadge tone={row.available ? 'success' : 'neutral'}>
                  {row.available ? 'Published' : 'Planned'}
                </StatusBadge>
              ),
            },
          ]}
          empty={
            <EmptyState
              title="No SDKs published"
              description="Client libraries are generated once the management API surface stabilises."
              availability="This capability is not available yet."
            />
          }
        />
      </SectionCard>
    </div>
  );
}
